import { useMemo, useState } from 'react';
import {
  Table,
  Input,
  Select,
  Tag,
  Space,
  Typography,
  Button,
  Spin,
  Modal,
  Popconfirm,
  message,
  Card,
  Tabs,
  Alert,
} from 'antd';
import { SearchOutlined, ReloadOutlined, DeleteOutlined, PlusOutlined, TagOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
  getMitigationTypeMappings,
  updateMitigationTypeMapping,
  getMitigationTypeMethodologyMappings,
  createMitigationTypeMethodologyMapping,
  updateMitigationTypeMethodologyMapping,
  deleteMitigationTypeMethodologyMapping,
  getMitigationTypeProjectIdMappings,
  createMitigationTypeProjectIdMapping,
  updateMitigationTypeProjectIdMapping,
  deleteMitigationTypeProjectIdMapping,
} from '../../api/mitigation_type';
import { getRegistryMethodologyPlatforms } from '../../api/registry_methodology';
import { getGHGStandards } from '../../api/ghg';

const { Text } = Typography;

const MITIGATION_TYPE_OPTIONS = [
  { value: 'Avoidance', label: 'Avoidance' },
  { value: 'Removals', label: 'Removals' },
  { value: 'Removals + Avoidance', label: 'Removals + Avoidance' },
];

type StatusType = 'mapped' | 'needs-mapping';

const STAT_CARD_SOFT_BG: Record<string, string> = {
  '#8c8c8c': '#f4f4f4',
  '#52c41a': '#f0fbe8',
  '#ff4d4f': '#fff1f1',
  '#1677ff': '#eef5ff',
};

function StatCards({
  stats,
}: {
  stats: { label: string; value: number; color: string; active?: boolean; onClick?: () => void }[];
}) {
  return (
    <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
      {stats.map((stat) => (
        <div
          key={stat.label}
          onClick={stat.onClick}
          style={{
            flex: '1 1 128px',
            minWidth: 128,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 8,
            background: stat.active ? STAT_CARD_SOFT_BG[stat.color] || '#f5f5f5' : '#fff',
            borderTop: stat.active ? `1px solid ${stat.color}` : '1px solid #f0f0f0',
            borderRight: stat.active ? `1px solid ${stat.color}` : '1px solid #f0f0f0',
            borderBottom: stat.active ? `1px solid ${stat.color}` : '1px solid #f0f0f0',
            borderLeft: `3px solid ${stat.color}`,
            borderRadius: 8,
            padding: '7px 12px',
            cursor: stat.onClick ? 'pointer' : 'default',
            transition: 'background 0.15s, border-color 0.15s',
          }}
        >
          <Text type="secondary" style={{ fontSize: 11.5 }}>
            {stat.label}
          </Text>
          <Text strong style={{ fontSize: 17, fontVariantNumeric: 'tabular-nums' }}>
            {stat.value}
          </Text>
        </div>
      ))}
    </div>
  );
}

function renderStatusTag(mapped: boolean) {
  return mapped ? <Tag color="green">Mapped</Tag> : <Tag color="red">Needs Mapping</Tag>;
}

async function fetchRegistryMethodologyPlatformsData() {
  const data = await getRegistryMethodologyPlatforms();
  const rows = Array.isArray(data) ? data : data.response || [];
  return rows as any[];
}

// ─── Tab 1: Project Type Based ─────────────────────────────────────────

const PROJECT_TYPE_QUERY_KEY = ['mitigationTypeMappings'];

function ProjectTypeBasedTab() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | StatusType>('all');

  const { data: rawData = [], isLoading, isFetching } = useQuery({
    queryKey: PROJECT_TYPE_QUERY_KEY,
    queryFn: async () => {
      const data = await getMitigationTypeMappings();
      const rows = data?.response ?? (Array.isArray(data) ? data : []);
      return rows as any[];
    },
  });

  const { mutate: updateMapping, isLoading: isUpdating } = useMutation(
    ({ calyx_project_type, mitigation_type }: { calyx_project_type: string; mitigation_type: string | null }) =>
      updateMitigationTypeMapping({ calyx_project_type, mitigation_type }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(PROJECT_TYPE_QUERY_KEY);
        message.success('Mapping updated');
      },
      onError: () => { message.error('Failed to update mapping. Please try again.'); },
    },
  );

  const filteredRows = useMemo(() => {
    const text = search.toLowerCase().trim();
    return (rawData as any[]).filter((row) => {
      const mapped = !!row.mitigation_type;
      if (statusFilter === 'mapped' && !mapped) return false;
      if (statusFilter === 'needs-mapping' && mapped) return false;
      if (!text) return true;
      const haystack = [
        row.project_type?.display_name || '',
        row.mitigation_type || '',
      ].join(' ').toLowerCase();
      return haystack.includes(text);
    });
  }, [rawData, search, statusFilter]);

  const columns: any[] = [
    {
      title: 'Calyx Project Type',
      key: 'project_type',
      sorter: (a: any, b: any) =>
        (a.project_type?.display_name || '').localeCompare(b.project_type?.display_name || ''),
      render: (_: any, record: any) => (
        <Text strong>{record.project_type?.display_name ?? '—'}</Text>
      ),
    },
    {
      title: 'Mitigation Type',
      key: 'mitigation_type',
      width: 260,
      sorter: (a: any, b: any) =>
        (a.mitigation_type || '').localeCompare(b.mitigation_type || ''),
      render: (_: any, record: any) => (
        <Select
          allowClear
          placeholder="Select mitigation type"
          value={record.mitigation_type || undefined}
          options={MITIGATION_TYPE_OPTIONS}
          onChange={(val) =>
            updateMapping({
              calyx_project_type: record.project_type?.display_name,
              mitigation_type: val ?? null,
            })
          }
          style={{ width: '100%' }}
        />
      ),
    },
    {
      title: 'Status',
      key: 'status',
      width: 130,
      sorter: (a: any, b: any) => {
        const sa = a.mitigation_type ? 'mapped' : 'needs-mapping';
        const sb = b.mitigation_type ? 'mapped' : 'needs-mapping';
        return sa.localeCompare(sb);
      },
      render: (_: any, record: any) => renderStatusTag(!!record.mitigation_type),
    },
  ];

  const needsMappingCount = (rawData as any[]).filter((r) => !r.mitigation_type).length;
  const mappedCount = (rawData as any[]).length - needsMappingCount;
  const loading = isLoading || isFetching || isUpdating;

  return (
    <Spin spinning={loading} tip="Loading…">
      <StatCards
        stats={[
          {
            label: 'Total Project Types',
            value: (rawData as any[]).length,
            color: '#8c8c8c',
            active: statusFilter === 'all',
            onClick: () => setStatusFilter('all'),
          },
          {
            label: 'Mapped',
            value: mappedCount,
            color: '#52c41a',
            active: statusFilter === 'mapped',
            onClick: () => setStatusFilter('mapped'),
          },
          {
            label: 'Needs Mapping',
            value: needsMappingCount,
            color: '#ff4d4f',
            active: statusFilter === 'needs-mapping',
            onClick: () => setStatusFilter('needs-mapping'),
          },
        ]}
      />
      <div style={{ margin: '0 0 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
        <Input
          prefix={<SearchOutlined />}
          placeholder="Search project type, mitigation type…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ maxWidth: 360 }}
          allowClear
        />
        <Space wrap>
          <Button
            icon={<ReloadOutlined />}
            onClick={() => queryClient.invalidateQueries(PROJECT_TYPE_QUERY_KEY)}
          >
            Refresh
          </Button>
        </Space>
      </div>
      <Table
        columns={columns}
        dataSource={filteredRows}
        rowKey={(record) => record.id || record.calyx_project_type_id || record.project_type?.display_name}
        size="middle"
        tableLayout="fixed"
        showSorterTooltip={false}
        pagination={{ defaultPageSize: 50, showSizeChanger: true }}
      />
    </Spin>
  );
}

// ─── Tab 2: Methodology Based Override ─────────────────────────────────

const METHODOLOGY_QUERY_KEY = ['mitigationTypeMethodologyMappings'];

function MethodologyBasedOverrideTab() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | StatusType>('all');
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [createRegistryMethodology, setCreateRegistryMethodology] = useState<string | null>(null);
  const [createMitigationType, setCreateMitigationType] = useState<string | null>(null);

  const { data: rawData = [], isLoading, isFetching } = useQuery({
    queryKey: METHODOLOGY_QUERY_KEY,
    queryFn: async () => {
      const data = await getMitigationTypeMethodologyMappings();
      const rows = data?.response ?? (Array.isArray(data) ? data : []);
      return rows as any[];
    },
  });

  const {
    data: registryMethodologies = [],
    isLoading: registryLoading,
    isFetching: registryFetching,
  } = useQuery({
    queryKey: ['registryMethodologyPlatforms'],
    queryFn: fetchRegistryMethodologyPlatformsData,
  });

  const registryMethodologyOptions = useMemo(
    () =>
      (registryMethodologies as any[]).map((m) => ({
        value: m.display_name,
        label: m.display_name,
      })),
    [registryMethodologies],
  );

  const { mutate: updateMapping, isLoading: isUpdating } = useMutation(
    ({ id, registry_methodology, mitigation_type }: { id: string; registry_methodology: string; mitigation_type: string | null }) =>
      updateMitigationTypeMethodologyMapping(id, { registry_methodology, mitigation_type }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(METHODOLOGY_QUERY_KEY);
        message.success('Mapping updated');
      },
      onError: () => { message.error('Failed to update mapping. Please try again.'); },
    },
  );

  const { mutate: deleteMapping, isLoading: isDeleting } = useMutation(
    (id: string) => deleteMitigationTypeMethodologyMapping(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(METHODOLOGY_QUERY_KEY);
        message.success('Override deleted');
      },
      onError: () => { message.error('Failed to delete override. Please try again.'); },
    },
  );

  const { mutate: createMapping, isLoading: isCreating } = useMutation(
    () =>
      createMitigationTypeMethodologyMapping({
        registry_methodology: createRegistryMethodology,
        ...(createMitigationType ? { mitigation_type: createMitigationType } : {}),
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(METHODOLOGY_QUERY_KEY);
        message.success('Override created');
        setCreateModalOpen(false);
        setCreateRegistryMethodology(null);
        setCreateMitigationType(null);
      },
      onError: (err: any) => { message.error(err?.message || 'Failed to create override. Please try again.'); },
    },
  );

  const filteredRows = useMemo(() => {
    const text = search.toLowerCase().trim();
    return (rawData as any[]).filter((row) => {
      const mapped = !!row.mitigation_type;
      if (statusFilter === 'mapped' && !mapped) return false;
      if (statusFilter === 'needs-mapping' && mapped) return false;
      if (!text) return true;
      const haystack = [
        row.registry_methodology_platform?.display_name || '',
        row.mitigation_type || '',
      ].join(' ').toLowerCase();
      return haystack.includes(text);
    });
  }, [rawData, search, statusFilter]);

  const columns: any[] = [
    {
      title: 'Harmonized Methodology',
      key: 'registry_methodology',
      sorter: (a: any, b: any) =>
        (a.registry_methodology_platform?.display_name || '').localeCompare(
          b.registry_methodology_platform?.display_name || '',
        ),
      render: (_: any, record: any) => (
        <Text strong>{record.registry_methodology_platform?.display_name ?? '—'}</Text>
      ),
    },
    {
      title: 'Mitigation Type',
      key: 'mitigation_type',
      width: 260,
      sorter: (a: any, b: any) => (a.mitigation_type || '').localeCompare(b.mitigation_type || ''),
      render: (_: any, record: any) => (
        <Select
          allowClear
          placeholder="Select mitigation type"
          value={record.mitigation_type || undefined}
          options={MITIGATION_TYPE_OPTIONS}
          onChange={(val) =>
            updateMapping({
              id: record.id,
              registry_methodology: record.registry_methodology_platform?.display_name,
              mitigation_type: val ?? null,
            })
          }
          style={{ width: '100%' }}
        />
      ),
    },
    {
      title: 'Status',
      key: 'status',
      width: 130,
      sorter: (a: any, b: any) => {
        const sa = a.mitigation_type ? 'mapped' : 'needs-mapping';
        const sb = b.mitigation_type ? 'mapped' : 'needs-mapping';
        return sa.localeCompare(sb);
      },
      render: (_: any, record: any) => renderStatusTag(!!record.mitigation_type),
    },
    {
      title: '',
      key: 'actions',
      width: 50,
      render: (_: any, record: any) => (
        <Popconfirm
          title="Delete this override?"
          okText="Delete"
          okType="danger"
          onConfirm={() => deleteMapping(record.id)}
        >
          <Button size="small" type="text" danger icon={<DeleteOutlined />} />
        </Popconfirm>
      ),
    },
  ];

  const needsMappingCount = (rawData as any[]).filter((r) => !r.mitigation_type).length;
  const mappedCount = (rawData as any[]).length - needsMappingCount;
  const loading = isLoading || isFetching || registryLoading || registryFetching || isUpdating || isDeleting;

  return (
    <Spin spinning={loading} tip="Loading…">
      <StatCards
        stats={[
          {
            label: 'Total Overrides',
            value: (rawData as any[]).length,
            color: '#8c8c8c',
            active: statusFilter === 'all',
            onClick: () => setStatusFilter('all'),
          },
          {
            label: 'Mapped',
            value: mappedCount,
            color: '#52c41a',
            active: statusFilter === 'mapped',
            onClick: () => setStatusFilter('mapped'),
          },
          {
            label: 'Needs Mapping',
            value: needsMappingCount,
            color: '#ff4d4f',
            active: statusFilter === 'needs-mapping',
            onClick: () => setStatusFilter('needs-mapping'),
          },
        ]}
      />
      <div style={{ margin: '0 0 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
        <Input
          prefix={<SearchOutlined />}
          placeholder="Search registry methodology, mitigation type…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ maxWidth: 360 }}
          allowClear
        />
        <Space wrap>
          <Button
            icon={<ReloadOutlined />}
            onClick={() => queryClient.invalidateQueries(METHODOLOGY_QUERY_KEY)}
          >
            Refresh
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setCreateModalOpen(true)}
          >
            Add Override
          </Button>
        </Space>
      </div>
      <Table
        columns={columns}
        dataSource={filteredRows}
        rowKey="id"
        size="middle"
        tableLayout="fixed"
        showSorterTooltip={false}
        pagination={{ defaultPageSize: 50, showSizeChanger: true }}
      />

      <Modal
        title="Add Methodology Override"
        open={createModalOpen}
        onCancel={() => {
          setCreateModalOpen(false);
          setCreateRegistryMethodology(null);
          setCreateMitigationType(null);
        }}
        onOk={() => createMapping()}
        okText="Create"
        confirmLoading={isCreating}
        okButtonProps={{ disabled: !createRegistryMethodology }}
      >
        <Space direction="vertical" style={{ width: '100%', marginTop: 8 }}>
          <div>
            <Text strong>Registry Methodology</Text>
            <Text type="danger"> *</Text>
            <Select
              allowClear
              style={{ width: '100%', marginTop: 4 }}
              placeholder="Select Registry Methodology"
              value={createRegistryMethodology || undefined}
              options={registryMethodologyOptions}
              onChange={(val) => setCreateRegistryMethodology(val ?? null)}
              showSearch
              optionFilterProp="label"
            />
          </div>
          <div>
            <Text strong>Mitigation Type</Text>
            <Text type="secondary"> (optional)</Text>
            <Select
              allowClear
              style={{ width: '100%', marginTop: 4 }}
              placeholder="Select mitigation type"
              value={createMitigationType || undefined}
              options={MITIGATION_TYPE_OPTIONS}
              onChange={(val) => setCreateMitigationType(val ?? null)}
            />
          </div>
        </Space>
      </Modal>
    </Spin>
  );
}

// ─── Tab 3: Project ID Based Override ──────────────────────────────────

const PROJECT_ID_QUERY_KEY = ['mitigationTypeProjectIdMappings'];

function ProjectIdBasedOverrideTab() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | StatusType>('all');
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [createProjectId, setCreateProjectId] = useState('');
  const [createGhgStandardCode, setCreateGhgStandardCode] = useState<string | null>(null);
  const [createMitigationType, setCreateMitigationType] = useState<string | null>(null);

  const { data: rawData = [], isLoading, isFetching } = useQuery({
    queryKey: PROJECT_ID_QUERY_KEY,
    queryFn: async () => {
      const data = await getMitigationTypeProjectIdMappings();
      const rows = data?.response ?? (Array.isArray(data) ? data : []);
      return rows as any[];
    },
  });

  const { data: ghgStandardsData, isLoading: ghgLoading } = useQuery({
    queryKey: ['ghgStandards'],
    queryFn: getGHGStandards,
  });

  const ghgStandardOptions = useMemo(() => {
    const standards = ghgStandardsData?.response ?? (Array.isArray(ghgStandardsData) ? ghgStandardsData : []);
    return (standards as any[]).map((s) => ({ value: s.code, label: s.code }));
  }, [ghgStandardsData]);

  const { mutate: updateMapping, isLoading: isUpdating } = useMutation(
    ({ id, mitigation_type }: { id: string; mitigation_type: string | null }) =>
      updateMitigationTypeProjectIdMapping(id, { mitigation_type }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(PROJECT_ID_QUERY_KEY);
        message.success('Mapping updated');
      },
      onError: () => { message.error('Failed to update mapping. Please try again.'); },
    },
  );

  const { mutate: deleteMapping, isLoading: isDeleting } = useMutation(
    (id: string) => deleteMitigationTypeProjectIdMapping(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(PROJECT_ID_QUERY_KEY);
        message.success('Override deleted');
      },
      onError: () => { message.error('Failed to delete override. Please try again.'); },
    },
  );

  const { mutate: createMapping, isLoading: isCreating } = useMutation(
    () =>
      createMitigationTypeProjectIdMapping({
        project_id: createProjectId.trim(),
        ghg_standard_code: createGhgStandardCode,
        ...(createMitigationType ? { mitigation_type: createMitigationType } : {}),
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(PROJECT_ID_QUERY_KEY);
        message.success('Override created');
        setCreateModalOpen(false);
        setCreateProjectId('');
        setCreateGhgStandardCode(null);
        setCreateMitigationType(null);
      },
      onError: (err: any) => { message.error(err?.message || 'Failed to create override. Please try again.'); },
    },
  );

  const handleMitigationTypeChange = (record: any, value: string | null) => {
    updateMapping({
      id: record.id,
      mitigation_type: value ?? null,
    });
  };

  const filteredRows = useMemo(() => {
    const text = search.toLowerCase().trim();
    return (rawData as any[]).filter((row) => {
      const mapped = !!row.mitigation_type;
      if (statusFilter === 'mapped' && !mapped) return false;
      if (statusFilter === 'needs-mapping' && mapped) return false;
      if (!text) return true;
      const haystack = [
        row.project_id || '',
        row.ghg_standard_code || '',
        row.mitigation_type || '',
      ].join(' ').toLowerCase();
      return haystack.includes(text);
    });
  }, [rawData, search, statusFilter]);

  const columns: any[] = [
    {
      title: 'Project ID',
      dataIndex: 'project_id',
      key: 'project_id',
      width: 160,
      sorter: (a: any, b: any) => (a.project_id || '').localeCompare(b.project_id || ''),
      render: (value: string) => <Text strong>{value ?? '—'}</Text>,
    },
    {
      title: 'Registry',
      dataIndex: 'ghg_standard_code',
      key: 'ghg_standard_code',
      width: 100,
      sorter: (a: any, b: any) => (a.ghg_standard_code || '').localeCompare(b.ghg_standard_code || ''),
    },
    {
      title: 'Mitigation Type',
      key: 'mitigation_type',
      width: 260,
      sorter: (a: any, b: any) => (a.mitigation_type || '').localeCompare(b.mitigation_type || ''),
      render: (_: any, record: any) => (
        <Select
          allowClear
          placeholder="Select mitigation type"
          value={record.mitigation_type || undefined}
          options={MITIGATION_TYPE_OPTIONS}
          onChange={(val) => handleMitigationTypeChange(record, val ?? null)}
          style={{ width: '100%' }}
        />
      ),
    },
    {
      title: 'Status',
      key: 'status',
      width: 130,
      sorter: (a: any, b: any) => {
        const sa = a.mitigation_type ? 'mapped' : 'needs-mapping';
        const sb = b.mitigation_type ? 'mapped' : 'needs-mapping';
        return sa.localeCompare(sb);
      },
      render: (_: any, record: any) => renderStatusTag(!!record.mitigation_type),
    },
    {
      title: '',
      key: 'actions',
      width: 50,
      render: (_: any, record: any) => (
        <Popconfirm
          title="Delete this override?"
          okText="Delete"
          okType="danger"
          onConfirm={() => deleteMapping(record.id)}
        >
          <Button size="small" type="text" danger icon={<DeleteOutlined />} />
        </Popconfirm>
      ),
    },
  ];

  const needsMappingCount = (rawData as any[]).filter((r) => !r.mitigation_type).length;
  const mappedCount = (rawData as any[]).length - needsMappingCount;
  const loading = isLoading || isFetching || ghgLoading || isUpdating || isDeleting;

  return (
    <Spin spinning={loading} tip="Loading…">
      <StatCards
        stats={[
          {
            label: 'Total Overrides',
            value: (rawData as any[]).length,
            color: '#8c8c8c',
            active: statusFilter === 'all',
            onClick: () => setStatusFilter('all'),
          },
          {
            label: 'Mapped',
            value: mappedCount,
            color: '#52c41a',
            active: statusFilter === 'mapped',
            onClick: () => setStatusFilter('mapped'),
          },
          {
            label: 'Needs Mapping',
            value: needsMappingCount,
            color: '#ff4d4f',
            active: statusFilter === 'needs-mapping',
            onClick: () => setStatusFilter('needs-mapping'),
          },
        ]}
      />
      <div style={{ margin: '0 0 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
        <Input
          prefix={<SearchOutlined />}
          placeholder="Search project ID, mitigation type…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ maxWidth: 360 }}
          allowClear
        />
        <Space wrap>
          <Button
            icon={<ReloadOutlined />}
            onClick={() => queryClient.invalidateQueries(PROJECT_ID_QUERY_KEY)}
          >
            Refresh
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setCreateModalOpen(true)}
          >
            Add Override
          </Button>
        </Space>
      </div>
      <Table
        columns={columns}
        dataSource={filteredRows}
        rowKey="id"
        size="middle"
        tableLayout="fixed"
        showSorterTooltip={false}
        pagination={{ defaultPageSize: 50, showSizeChanger: true }}
      />

      <Modal
        title="Add Project ID Override"
        open={createModalOpen}
        onCancel={() => {
          setCreateModalOpen(false);
          setCreateProjectId('');
          setCreateGhgStandardCode(null);
          setCreateMitigationType(null);
        }}
        onOk={() => createMapping()}
        okText="Create"
        confirmLoading={isCreating}
        okButtonProps={{ disabled: !createProjectId.trim() || !createGhgStandardCode }}
      >
        <Space direction="vertical" style={{ width: '100%', marginTop: 8 }}>
          <div>
            <Text strong>Project ID</Text>
            <Text type="danger"> *</Text>
            <Input
              style={{ marginTop: 4 }}
              placeholder="Enter project ID"
              value={createProjectId}
              onChange={(e) => setCreateProjectId(e.target.value)}
            />
          </div>
          <div>
            <Text strong>Registry</Text>
            <Text type="danger"> *</Text>
            <Select
              style={{ width: '100%', marginTop: 4 }}
              placeholder="Select registry"
              value={createGhgStandardCode || undefined}
              options={ghgStandardOptions}
              onChange={(val) => setCreateGhgStandardCode(val ?? null)}
              showSearch
              optionFilterProp="label"
            />
          </div>
          <div>
            <Text strong>Mitigation Type</Text>
            <Text type="secondary"> (optional)</Text>
            <Select
              allowClear
              style={{ width: '100%', marginTop: 4 }}
              placeholder="Select mitigation type"
              value={createMitigationType || undefined}
              options={MITIGATION_TYPE_OPTIONS}
              onChange={(val) => setCreateMitigationType(val ?? null)}
            />
          </div>
        </Space>
      </Modal>
    </Spin>
  );
}

// ─── Main Component with Tabs ──────────────────────────────────────────

function MitigationTypeMapper() {
  return (
    <div style={{ padding: 24, background: '#f5f5f5', minHeight: '100vh', boxSizing: 'border-box' }}>
      <div
        style={{
          marginBottom: 16,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 16,
          flexWrap: 'wrap',
        }}
      >
        <div style={{ flex: '1 1 auto' }}>
          <Typography.Title level={3} style={{ marginBottom: 2 }}>
            <TagOutlined style={{ marginRight: 8 }} />
            Mitigation Type Mapper
          </Typography.Title>
          <Text type="secondary">
            Map mitigation types by Calyx project type, registry methodology, or individual project ID.
          </Text>
        </div>
        <Alert
          type="info"
          showIcon
          closable
          message="Please note that changes saved here won't appear on the platform immediately. They will be applied during the next automatic registry sync, which runs every Saturday."
          style={{ width: '50%', flexShrink: 0 }}
        />
      </div>
      <Card
        bordered={false}
        style={{ borderRadius: 10, boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}
      >
        <Tabs
          defaultActiveKey="project-type"
          type="card"
          items={[
            {
              key: 'project-type',
              label: 'Project Type Based',
              children: <ProjectTypeBasedTab />,
            },
            {
              key: 'methodology-override',
              label: 'Methodology Based Override',
              children: <MethodologyBasedOverrideTab />,
            },
            {
              key: 'project-id-override',
              label: 'Project ID Based Override',
              children: <ProjectIdBasedOverrideTab />,
            },
          ]}
        />
      </Card>
    </div>
  );
}

export default MitigationTypeMapper;
